import * as React from "react";
import { Link, useNavigate } from "react-router-dom";

import { useStore } from "@/context/StoreContext";
import { AnnouncementBar } from "@/components/store/AnnouncementBar";
import { StoreHeroCarousel } from "@/components/store/StoreHeroCarousel";
import { CategoryTabs } from "@/components/store/CategoryTabs";
import { GameNews } from "@/components/store/GameNews";
import { ToolCards } from "@/components/store/ToolCards";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { useFirstClickAd } from "@/hooks/useFirstClickAd";
import { useAdmin } from "@/context/AdminContext";
import { GameGridSkeleton } from "@/components/common/GameCardSkeleton";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SmartImage } from "@/components/common/SmartImage";

import { Heart, Info, Send, AlertTriangle, Star, MessageCircle, Sparkles, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

// Lazy load heavy components - only load when needed
const AdminLoginDialog = React.lazy(() => import("@/components/admin/AdminLoginDialog").then(m => ({ default: m.AdminLoginDialog })));

const normalize = (s) => String(s ?? "").trim().toLowerCase();

// Generate URL slug from title - supports Turkish and special characters
const generateSlug = (title) => {
  if (!title) return "";
  
  // Turkish character map
  const turkishMap = {
    'ı': 'i', 'ğ': 'g', 'ü': 'u', 'ş': 's', 'ö': 'o', 'ç': 'c',
    'İ': 'i', 'Ğ': 'g', 'Ü': 'u', 'Ş': 's', 'Ö': 'o', 'Ç': 'c',
    'â': 'a', 'î': 'i', 'û': 'u', 'Â': 'a', 'Î': 'i', 'Û': 'u'
  };
  
  return title
    .toLowerCase()
    .split('')
    .map(c => turkishMap[c] || c)
    .join('')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars except space and dash
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/-+/g, '-') // Replace multiple dashes with single
    .replace(/^-|-$/g, '') // Remove leading/trailing dashes
    .trim() + '-indir';
};

// Check if game is new (within 12 hours)
const isNewGame = (createdAt) => {
  if (!createdAt) return false;
  const now = Date.now();
  const twelveHours = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
  return (now - createdAt) < twelveHours;
};

// Get hours ago text
const getHoursAgo = (createdAt) => {
  if (!createdAt) return "";
  const now = Date.now();
  const diffMs = now - createdAt;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return "Az önce";
  return `${hours} saat önce`;
};

// Simple New Badge Component - No animation for better performance
const NewGameBadge = React.memo(({ createdAt }) => {
  const hoursText = getHoursAgo(createdAt);
  
  return (
    <div className="absolute -top-0.5 -right-0.5 z-10">
      <div className="relative">
        <div className="bg-gradient-to-r from-green-600 to-emerald-500 text-white text-[9px] sm:text-[11px] font-bold py-1 sm:py-1.5 px-3 sm:px-5 rounded-bl-lg rounded-tr-2xl shadow-lg">
          <span className="inline-flex items-center gap-1">
            <Sparkles className="size-3 sm:size-3.5" />
            YENİ • {hoursText}
          </span>
        </div>
        <div className="absolute -bottom-1.5 right-0 w-0 h-0 border-l-[10px] border-l-transparent border-t-[10px] border-t-green-800"></div>
      </div>
    </div>
  );
});

export default function StorePage() {
  const { categories, games, toggleFavorite, telegramUrl, maintenanceMode, maintenanceMessage, isLoading, isLoaded, pagination, gamesLoading, loadGamesPage, adLinkEnabled, adLinkUrl } = useStore();
  const { isAdmin } = useAdmin();
  
  // First click ad hook
  useFirstClickAd(adLinkEnabled, adLinkUrl);

  // Scroll to top on initial page load - ensure it happens after content loads
  React.useEffect(() => {
    // Use multiple strategies to ensure scroll to top works
    const scrollToTop = () => {
      // Method 1: Standard scroll
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      
      // Method 2: Direct property setting
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Method 3: Force scroll using requestAnimationFrame
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
    };
    
    // Immediate scroll
    scrollToTop();
    
    // Also scroll after short delays to ensure it sticks
    const timeouts = [50, 100, 200, 500].map(delay => 
      setTimeout(scrollToTop, delay)
    );
    
    return () => timeouts.forEach(clearTimeout);
  }, []);

  // Get page from URL using native API for reliability
  const getPageFromUrl = React.useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return parseInt(params.get("page") || "1", 10);
  }, []);
  
  const [currentPage, setCurrentPage] = React.useState(getPageFromUrl);
  const [tab, setTab] = React.useState(categories?.[0]?.id ?? "new");
  const [searchQuery, setSearchQuery] = React.useState("");
  
  // Reference to games section for scroll
  const gamesSectionRef = React.useRef(null);

  // Update URL without full page reload
  const updateUrlPage = React.useCallback((page) => {
    const url = new URL(window.location.href);
    url.searchParams.set("page", String(page));
    window.history.pushState({ page }, "", url.toString());
  }, []);

  // Listen for browser back/forward navigation
  React.useEffect(() => {
    const handlePopState = (event) => {
      const page = getPageFromUrl();
      setCurrentPage(page);
    };
    
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [getPageFromUrl]);

  const handleSearch = React.useCallback((query) => {
    setSearchQuery(query);
    setCurrentPage(1);
    updateUrlPage(1);
  }, [updateUrlPage]);

  const clearSearch = React.useCallback(() => {
    setSearchQuery("");
    setCurrentPage(1);
    updateUrlPage(1);
  }, [updateUrlPage]);

  React.useEffect(() => {
    if (!categories?.length) return;
    if (!tab?.startsWith("__") && !categories.find((c) => c.id === tab)) setTab(categories[0].id);
  }, [categories, tab]);

  // Track if initial load happened
  const initialLoadDone = React.useRef(false);
  const lastSearchRef = React.useRef("");

  // Load games when page or search changes
  React.useEffect(() => {
    if (!isLoaded) return;
    
    // Always reload when search changes
    const searchChanged = lastSearchRef.current !== searchQuery;
    lastSearchRef.current = searchQuery;
    
    // Scroll to games section when page changes (not on initial load)
    if (initialLoadDone.current && gamesSectionRef.current && currentPage > 1 && !searchChanged) {
      gamesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    initialLoadDone.current = true;
    
    // Get category ID for the selected tab
    const isSpecialTab = tab?.startsWith("__");
    let categoryId = null;
    let sortParam = null;

    if (tab === "__popular") {
      sortParam = "popular";
    } else if (tab === "__top_rated") {
      sortParam = "rating";
    } else {
      const selectedCat = categories.find((c) => c.id === tab);
      categoryId = (selectedCat && selectedCat.slug !== 'all') ? selectedCat.id : null;
    }
    
    // Load games with search query if present
    loadGamesPage(currentPage, categoryId, searchQuery || null, sortParam);
  }, [currentPage, isLoaded, categories, tab, loadGamesPage, searchQuery]);

  // Load games when tab changes
  React.useEffect(() => {
    if (!isLoaded) return;
    
    // Reset to page 1 when tab changes
    setCurrentPage(1);
    updateUrlPage(1);
  }, [tab, isLoaded, updateUrlPage]);

  const activeTitle = categories.find((c) => c.id === tab)?.title;

  // Handle page change - update URL, state and scroll to games section
  const handlePageChange = React.useCallback((newPage) => {
    // Scroll to games section (not top of page)
    if (gamesSectionRef.current) {
      const headerOffset = 100; // Account for fixed header
      const elementPosition = gamesSectionRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'instant'
      });
    }
    
    // Then update state and URL
    setCurrentPage(newPage);
    updateUrlPage(newPage);
  }, [updateUrlPage]);

  // Games are now filtered by API, no frontend filtering needed
  // Programs are now shown on a separate page (/programlar)
  const displayedGames = React.useMemo(() => {
    return games;
  }, [games]);

  // Use pagination from API
  const pageCount = pagination.totalPages || 1;
  const safePage = currentPage;

  // Listen to search events from SiteTopBar (AppLayout)
  React.useEffect(() => {
    const onSearchEvent = (e) => {
      const q = String(e?.detail ?? "");
      if (q.trim()) handleSearch(q.trim());
      else clearSearch();
    };
    window.addEventListener("store:search", onSearchEvent);
    return () => window.removeEventListener("store:search", onSearchEvent);
  }, [handleSearch, clearSearch]);

  // Listen to footer category click events
  React.useEffect(() => {
    const onCategoryEvent = (e) => {
      const categoryId = e?.detail;
      if (categoryId) {
        setTab(categoryId);
      }
    };
    window.addEventListener("footer:category", onCategoryEvent);
    return () => window.removeEventListener("footer:category", onCategoryEvent);
  }, [setTab]);

  const [adminLoginOpen, setAdminLoginOpen] = React.useState(false);

  React.useEffect(() => {
    const handler = () => {
      if (isAdmin) {
        // Navigate to admin page
        window.location.href = "/admin";
      } else {
        setAdminLoginOpen(true);
      }
    };
    window.addEventListener("delay:open-admin", handler);
    return () => window.removeEventListener("delay:open-admin", handler);
  }, [isAdmin]);

  const handleAdminClick = () => {
    if (isAdmin) {
      window.location.href = "/admin";
    } else {
      setAdminLoginOpen(true);
    }
  };

  return (
    <div className="min-h-screen">
      <AnnouncementBar onOpenAdmin={handleAdminClick} />

      <div className="mt-4">
        <div className="min-w-0">
          <StoreHeroCarousel />

          {/* Tool Cards - Centered below slider */}
          <ToolCards />

          {/* Game News Section */}
          <GameNews />

          <div className="mt-6">
            <CategoryTabs categories={categories} value={tab} onChange={setTab} />
          </div>

          {/* Search Results Header */}
          {searchQuery && (
            <div className="mt-4 flex items-center justify-between p-3 rounded-xl bg-primary/10 border border-primary/20">
              <div className="flex items-center gap-2">
                <Search className="size-4 text-primary" />
                <span className="text-sm">
                  <strong>&quot;{searchQuery}&quot;</strong> için {displayedGames.length} sonuç bulundu
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={clearSearch} className="gap-1">
                <X className="size-3" />
                Temizle
              </Button>
            </div>
          )}

          <div ref={gamesSectionRef} data-testid="games-grid" className="mt-4 sm:mt-5 grid grid-cols-2 gap-2 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {/* Skeleton Loading - Show instantly while loading */}
            {(isLoading || gamesLoading) && (
              <GameGridSkeleton count={16} />
            )}
            
            {/* Actual Games - Show when loaded */}
            {!isLoading && !gamesLoading && displayedGames.map((g) => {
              const gameIsNew = isNewGame(g.createdAt);
              const rating = Math.min(5, Math.max(0, Number(g.rating) || 0));
              const commentCount = g.comments?.length || 0;
              const itemSlug = generateSlug(g.title);
              
              return (
                <Link
                  key={g.id}
                  to={`/${itemSlug}`}
                  className="block"
                >
                  <Card
                    className="group overflow-hidden rounded-2xl sm:rounded-3xl border-white/10 bg-white/5 cursor-pointer relative h-full shadow-[0_4px_30px_rgba(0,0,0,0.1)]"
                  >
                    {/* New Game Badge - Animated */}
                    {gameIsNew && <NewGameBadge createdAt={g.createdAt} />}
                    
                    <div className="block w-full text-left">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-background/65 via-background/0 to-background/0" />
                        <div className="aspect-[4/3]">
                          <SmartImage
                            alt={g.title}
                            src={g.cover}
                            className="h-full w-full"
                            imgClassName="object-cover"
                            aspectRatio="video"
                            loading={displayedGames.indexOf(g) < 6 ? "eager" : "lazy"}
                          />
                        </div>

                        <div className="absolute left-3 sm:left-4 top-3 sm:top-4 flex items-center gap-1.5 sm:gap-2">
                          <Badge variant="info" className="text-[10px] sm:text-xs">{g.category}</Badge>
                          <Badge variant="secondary" className="text-[10px] sm:text-xs">{(g.downloads ?? 0).toLocaleString()} DL</Badge>
                        </div>
                      </div>

                      <div className="p-3 sm:p-4 bg-white/5 border-t border-white/10">
                        <div className="truncate font-medium text-sm sm:text-base">{g.title}</div>
                        <div className="mt-1 line-clamp-2 text-xs sm:text-sm text-muted-foreground">
                          {g.description}
                        </div>

                        <div className="mt-3 sm:mt-4 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <Button
                              variant="glass"
                              size="sm"
                              className="gap-1.5 sm:gap-2 rounded-xl sm:rounded-2xl h-8 sm:h-9 text-xs sm:text-sm px-3 sm:px-4"
                              onClick={(e) => {
                                e.preventDefault();
                                // Navigate handled by Link wrapper
                              }}
                            >
                              <Info className="size-3.5 sm:size-4" />
                              Detay
                            </Button>
                            
                            {/* Star Rating - Bigger */}
                            <div className="flex items-center gap-0.5 sm:gap-1">
                              {[1, 2, 3, 4, 5].map((i) => (
                                <Star
                                  key={i}
                                  className={cn(
                                    "size-4 sm:size-5",
                                    i <= Math.round(rating) 
                                      ? "text-yellow-400 fill-yellow-400 drop-shadow-[0_0_3px_rgba(250,204,21,0.5)]" 
                                      : "text-muted-foreground/40"
                                  )}
                                />
                              ))}
                              {g.ratingCount > 0 && (
                                <span className="text-xs text-muted-foreground ml-1">({g.ratingCount})</span>
                              )}
                            </div>
                            
                            {/* Comment Count - From API */}
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <MessageCircle className="size-3.5 sm:size-4" />
                              <span className="text-xs sm:text-sm font-medium">{g.commentCount || commentCount}</span>
                            </div>
                          </div>

                          <Button
                            variant="glass"
                            size="icon"
                            className={cn(
                              "rounded-xl sm:rounded-2xl size-8 sm:size-9",
                              g.isFavorite && "bg-red-500/20 text-red-500 hover:bg-red-500/30"
                            )}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleFavorite(g.id);
                            }}
                            aria-label="Favorite"
                          >
                            <Heart className={cn(
                              "size-3.5 sm:size-4",
                              g.isFavorite && "fill-red-500 text-red-500"
                            )} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}

            {!isLoading && !gamesLoading && !displayedGames.length ? (
              <Card className="col-span-full rounded-3xl border-border/60 bg-card p-6 text-sm text-muted-foreground shadow-soft">
                {searchQuery ? "Arama sonucu bulunamadı." : "Bu kategoride oyun bulunamadı."}
              </Card>
            ) : null}
          </div>

          {/* Pagination */}
          {pageCount > 1 && !searchQuery && (
            <div className="mt-6 flex flex-col items-center gap-3">
              <div className="flex items-center gap-2">
                <Button
                  variant="glass"
                  size="sm"
                  className="rounded-xl"
                  disabled={safePage <= 1 || gamesLoading}
                  onClick={() => handlePageChange(Math.max(1, safePage - 1))}
                >
                  Önceki
                </Button>

                <div className="flex items-center gap-1 flex-wrap justify-center">
                  {/* First page */}
                  {safePage > 3 && (
                    <>
                      <Button
                        variant="glass"
                        size="sm"
                        className="h-9 min-w-9 rounded-xl px-3"
                        disabled={gamesLoading}
                        onClick={() => handlePageChange(1)}
                      >
                        1
                      </Button>
                      {safePage > 4 && <span className="px-1 text-muted-foreground">…</span>}
                    </>
                  )}
                  
                  {/* Pages around current */}
                  {Array.from({ length: pageCount }, (_, i) => i + 1)
                    .filter(p => p >= safePage - 2 && p <= safePage + 2)
                    .map((p) => (
                      <Button
                        key={p}
                        variant={p === safePage ? "hero" : "glass"}
                        size="sm"
                        className="h-9 min-w-9 rounded-xl px-3"
                        disabled={gamesLoading}
                        onClick={() => handlePageChange(p)}
                      >
                        {p}
                      </Button>
                    ))}

                  {/* Last page */}
                  {safePage < pageCount - 2 && (
                    <>
                      {safePage < pageCount - 3 && <span className="px-1 text-muted-foreground">…</span>}
                      <Button
                        variant="glass"
                        size="sm"
                        className="h-9 min-w-9 rounded-xl px-3"
                        disabled={gamesLoading}
                        onClick={() => handlePageChange(pageCount)}
                      >
                        {pageCount}
                      </Button>
                    </>
                  )}
                </div>

                <Button
                  variant="glass"
                  size="sm"
                  className="rounded-xl"
                  disabled={safePage >= pageCount || gamesLoading}
                  onClick={() => handlePageChange(Math.min(pageCount, safePage + 1))}
                >
                  Sonraki
                </Button>
              </div>

              <div className="text-center text-sm text-muted-foreground">
                {pagination.total ? (
                  <>Sayfa {safePage} / {pageCount} — Toplam {pagination.total} oyun</>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Telegram Button - Moves with page, positioned in content */}
      {telegramUrl && (
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6">
          <a
            href={telegramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#0088cc] to-[#0055aa] text-white text-sm sm:text-base"
            aria-label="Telegram"
          >
            <div className="relative">
              <Send className="size-5 sm:size-6" />
              <div className="absolute -top-1 -right-1 size-3 sm:size-4 rounded-full bg-red-500 text-white text-[8px] sm:text-[10px] font-bold flex items-center justify-center">
                !
              </div>
            </div>
            <span className="font-medium">Telegram Kanalımız</span>
          </a>
        </div>
      )}

      {/* Advanced Footer */}
      <SiteFooter />

      {/* Maintenance Mode Overlay */}
      {maintenanceMode && !isAdmin && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 backdrop-blur-sm">
          <div className="text-center p-8 max-w-md">
            <div className="grid size-20 place-items-center rounded-full bg-yellow-500/20 mx-auto mb-6">
              <AlertTriangle className="size-10 text-yellow-500" />
            </div>
            <h1 className="text-2xl font-bold mb-4">Bakım Modu</h1>
            <p className="text-muted-foreground whitespace-pre-wrap">
              {maintenanceMessage || "Site şu anda bakım modundadır. Lütfen daha sonra tekrar deneyiniz."}
            </p>
            {telegramUrl && (
              <a
                href={telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-[#0088cc] text-white hover:bg-[#0077b5] transition-colors"
              >
                <Send className="size-5" />
                Telegram&apos;dan Takip Edin
              </a>
            )}
          </div>
        </div>
      )}

      {/* Lazy loaded modals - only render when needed */}
      {adminLoginOpen && (
        <React.Suspense fallback={null}>
          <AdminLoginDialog open={adminLoginOpen} onOpenChange={setAdminLoginOpen} />
        </React.Suspense>
      )}

    </div>
  );
}
